import React, { useEffect, useRef } from 'react'
import { Button, DialogOverlayActions, Row, TransitioningDialogOverlay } from '@fs/zion-ui'
import { Route } from '@fs/zion-router'
import { useTranslation } from 'react-i18next'
import { trackEvent } from '@fs/zion-analytics'
import StoryTabs from '../StoryTabs/StoryTabs'
import UpdateMemoriesView from '../UpdateMemoriesView/UpdateMemoriesView'
import useSaveStoryLogic from '../../../../../hooks/useSaveStoryLogic/useSaveStoryLogic'

import { useStoryManager } from '../../../../../providers/StoryProvider'

// Routes
const STORY_PICKER_PATH = '/story-picker'
const CONFIRMATION_PATH = '/confirm'

export default function TextPickerOverlay({
  overlay,
  positionIndex,
  pageNumber,
  handleMemoryUpdate,
  customLayoutElementId,
  onUpdateCustomLayoutElementData,
}) {
  const [t] = useTranslation()
  const { storyState, resetOriginalGalleryStory, setTab, pendingEdits, setPendingEdits, hasStoryChanged, saveHandler } =
    useStoryManager()
  const { currentTab } = storyState

  const { saveEditStory, saveEditedGalleryMemory } = useSaveStoryLogic({
    pageNumber,
    positionIndex,
    customLayoutElementId,
    onUpdateCustomLayoutElementData,
  })

  // In your overlay component
  const originalStoryState = useRef(null)

  useEffect(() => {
    if (overlay.isOpen) {
      originalStoryState.current = { ...storyState }
    }
  }, [overlay.isOpen, storyState])

  const handleOverlayClose = () => {
    // Reset unsaved changes when closing without saving
    if (hasStoryChanged()) {
      setPendingEdits({
        storyId: storyState?.storyId || null,
        title: storyState?.title || '',
        description: storyState?.description || '',
        isValidStory: false,
        isGalleryStory: false,
      })
    }
    resetOriginalGalleryStory()
    setTab(0)
  }
  const handleEditSave = async (e) => {
    try {
      if (typeof saveHandler === 'function') {
        saveHandler() // commits the edits from StoryTextForm
      }

      overlay.close(e)

      await saveEditStory()

      trackEvent({
        event_name: 'memories_create_story',
      })
    } catch (error) {
      console.error('Error saving story:', error)
    } finally {
      handleMemoryUpdate(false)
    }
  }

  const handleEditedGalleryMemory = async (updateMemory = false) => {
    try {
      if (typeof saveHandler === 'function') {
        saveHandler() // commits the edits from StoryTextForm
      }

      overlay.close()
      await saveEditedGalleryMemory(updateMemory)
      trackEvent({
        event_name: 'memories_save_edited_story',
      })
    } catch (error) {
      console.error('Error saving gallery memory:', error)
    } finally {
      handleMemoryUpdate(true)
    }
  }

  const getSaveButtonProps = () => {
    if (pendingEdits?.isGalleryStory && pendingEdits?.storyId && hasStoryChanged()) {
      return { to: CONFIRMATION_PATH, disabled: !pendingEdits?.isValidStory }
    }
    return {
      onClick: handleEditSave,
      disabled: !pendingEdits?.isValidStory,
    }
  }

  return (
    <TransitioningDialogOverlay
      title={t('TextPicker.editor.title', 'Story Editor')}
      size="lg"
      onClose={handleOverlayClose}
      {...overlay}
      initialLocation={STORY_PICKER_PATH}
    >
      <Route
        path={STORY_PICKER_PATH}
        subtitle={t('TextPicker.editText.subtitle', 'Edit Title and Story')}
        footer={
          <DialogOverlayActions>
            <Row>
              <Button onClick={overlay.close}>{t('common-ui:cancel.action', 'Cancel')}</Button>
              {currentTab === 0 ? (
                <Button {...getSaveButtonProps()} emphasis="high">
                  {t('common-ui:save.action', 'Save')}
                </Button>
              ) : (
                <Button emphasis="high" onClick={() => setTab(0)} disabled={!pendingEdits?.storyId}>
                  {t('TextPicker.editStory.label', 'Edit Story')}
                </Button>
              )}
            </Row>
          </DialogOverlayActions>
        }
      >
        <StoryTabs />
      </Route>
      <Route
        subtitle={t('TextPicker.confirmAndSave.label', 'Confirm and Save')}
        path={CONFIRMATION_PATH}
        footer={
          <DialogOverlayActions>
            <Row wrap alignX="end">
              <Button onClick={() => handleEditedGalleryMemory(true)} linkName="Button:Save Over Memory">
                {t('TextPicker.saveOverMemory.label', 'Save Over Existing Memory')}
              </Button>
              <Button
                emphasis="high"
                onClick={() => handleEditedGalleryMemory(false)}
                linkName="Button:Save New Memory"
              >
                {t('TextPicker.saveNewMemory.label', 'Save as New Memory')}
              </Button>
            </Row>
          </DialogOverlayActions>
        }
      >
        <UpdateMemoriesView />
      </Route>
    </TransitioningDialogOverlay>
  )
}
